import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { readLocationIds } from './locationParam'
import { ACTION_QUEUES, ActionQueueDef, QueueFreshnessSegment, QueueValueStats } from './queueRegistry'
import { MomentumSummary, useActionCenterMomentum } from './momentumStore'
import { useQueueValueStats } from './useQueueValueStats'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { usePmsIntegration } from 'app/hooks/usePmsIntegration'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { ContactJson } from 'app/types/entities'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { buildCountQuery, readCounts } from 'lib/countQuery'
import { logError } from 'lib/logging'

export type QueueCardVM = {
  def: ActionQueueDef
  count?: number
  perItemValue?: number
  snoozedCount: number
  freshness: { segment: QueueFreshnessSegment; count: number }[]
  /** Undefined while loading, or for a queue whose card lists nobody. */
  preview?: { contact: ContactJson; enteredAt?: string }[]
}

export type UseActionCenterReturn = {
  user: ReturnType<typeof useUser>
  workspace: ReturnType<typeof useWorkspace>
  greeting: string
  queues: QueueCardVM[]
  momentum: MomentumSummary
  valueStats: QueueValueStats
  loadingValues: boolean
}

function greetingForNow(name?: string): string {
  const hour = new Date().getHours()
  const timeOfDay = hour < 12 ? `Good morning` : hour < 17 ? `Good afternoon` : `Good evening`
  const firstName = name?.split(` `)[0]
  return firstName ? `${timeOfDay}, ${firstName}` : timeOfDay
}

export function useActionCenter(): UseActionCenterReturn {
  const user = useUser()
  const workspace = useWorkspace()
  const hygieneEnabled = useFeatureToggle(`hygiene`)
  const { treatmentGate } = usePmsIntegration()
  const needsActionDateEnabled = useFeatureToggle(`needs-action-date`)
  const { counts } = useSidebarCounts()
  const [searchParams] = useSearchParams()
  // Location drill-down — the standalone LocationFilter encodes into `filters`.
  const filtersParam = searchParams.get(`filters`)
  const locationIds = useMemo(() => readLocationIds(filtersParam), [filtersParam])
  const locationTerm = locationIds.length
    ? `locationId:(${locationIds.map((id) => `"${id}"`).join(` OR `)})`
    : undefined
  const { stats: valueStats, loading: loadingValues } = useQueueValueStats(workspace?.id, locationIds)
  const { summary: momentum, snoozes } = useActionCenterMomentum(user?.id, workspace?.id)
  const [queueTotals, setQueueTotals] = useState<Record<string, number>>({})
  const [previews, setPreviews] = useState<Record<string, ContactJson[]>>({})

  const visibleQueues = useMemo(
    () =>
      ACTION_QUEUES.filter((queue) => {
        // Only the hygiene bundle rides the hygiene toggle; the treatment
        // queues shared its gate by accident of both being patient-family,
        // which turned "hygiene off" into "treatment invisible" too. Patient
        // queues of any kind still need a workspace to count against.
        if (queue.bundle === `hygiene`) return !!hygieneEnabled && !!workspace
        if (queue.bundle === `treatment` && treatmentGate === `hidden`) return false
        if (queue.family === `patient`) return !!workspace
        return true
      }),
    [hygieneEnabled, workspace, treatmentGate],
  )

  // Queue totals — one batched aliased query, same pattern as the custom-page
  // counts in useSidebarCounts. Totals come from here (not the sidebar) so the
  // location drill-down filters them.
  useEffect(() => {
    if (!user?.id || visibleQueues.length === 0) return
    setQueueTotals({})
    const withLocation = (terms: string[]) => (locationTerm ? [...terms, locationTerm] : terms)
    const opts = { needsActionDateEnabled: !!needsActionDateEnabled }
    // Modules that count from staged data sit out of the contacts query: asking
    // `contacts` for them would return the whole workspace.
    const built = buildCountQuery(
      `ActionCenterQueueCounts`,
      visibleQueues
        .filter((queue) => !queue.stagedCount)
        .flatMap((queue) => {
          const terms = queue.searchTerms(opts)
          return [
            { key: queue.id, search: withLocation(terms).join(` `) },
            ...queue.freshness.map((segment) => ({
              key: `${queue.id}:${segment.id}`,
              search: withLocation([...terms, ...segment.extraTerms(opts)]).join(` `),
            })),
          ]
        }),
    )
    if (!built) return
    bg(
      gqlQuery<Record<string, { total: number }>>(built.query, built.variables)
        .then(({ data }) => setQueueTotals(readCounts(data, built.aliases)))
        .catch((err) => logError(`Error fetching Action Center queue counts`, err)),
    )
  }, [user?.id, workspace?.id, needsActionDateEnabled, visibleQueues, locationTerm])

  // Snoozed contacts are dropped after the read, so it asks for enough extra rows
  // to still fill the preview if every snooze is near the top.
  useEffect(() => {
    if (!user?.id) return
    setPreviews({})
    let stale = false
    const opts = { needsActionDateEnabled: !!needsActionDateEnabled }
    for (const queue of visibleQueues) {
      if (!queue.previewCount) continue
      const terms = queue.searchTerms(opts)
      bg(
        gqlFetchList<ContactJson>(`contacts`, {
          search: (locationTerm ? [...terms, locationTerm] : terms).join(` `),
          pagination: { page: 0, limit: queue.previewCount + snoozes.length },
          sort: queue.sort(opts),
        })
          .then(({ hits }) => {
            if (!stale) setPreviews((prev) => ({ ...prev, [queue.id]: hits }))
          })
          .catch((err) => logError(`Error fetching Action Center queue preview`, err)),
      )
    }
    return () => {
      stale = true
    }
  }, [user?.id, workspace?.id, needsActionDateEnabled, visibleQueues, locationTerm, snoozes.length])

  const queues = useMemo<QueueCardVM[]>(
    () =>
      visibleQueues.map((def) => {
        // Own totals (location-aware); the sidebar's unfiltered count fills in
        // while they load, but never when a location filter is active.
        const count =
          def.stagedCount?.() ?? queueTotals[def.id] ?? (locationIds.length ? undefined : counts.contacts?.[def.id])
        const perItemValue = def.perItemValue(valueStats)
        const snoozedCount = snoozes.filter((s) => s.queueId === def.id).length
        const effectiveCount = count != null ? Math.max(0, count - snoozedCount) : undefined
        // Snoozes are not excluded from the segment search, so cap a segment at the
        // waiting count it is part of.
        const freshness = def.freshness.flatMap((segment) => {
          const segmentCount = queueTotals[`${def.id}:${segment.id}`]
          if (segmentCount == null || effectiveCount == null) return []
          return [{ segment, count: Math.min(segmentCount, effectiveCount) }]
        })
        const snoozedIds = new Set(snoozes.filter((s) => s.queueId === def.id).map((s) => s.contactId))
        const enteredField = def.sort({ needsActionDateEnabled: !!needsActionDateEnabled }).field
        const preview = previews[def.id]
          ?.filter((contact) => contact.id && !snoozedIds.has(contact.id))
          .slice(0, def.previewCount)
          .map((contact) => {
            const entered = (contact as Record<string, unknown>)[enteredField]
            return { contact, enteredAt: typeof entered === `string` ? entered : undefined }
          })
        return {
          def,
          count: effectiveCount,
          perItemValue,
          snoozedCount,
          freshness,
          preview,
        }
      }),
    [
      visibleQueues,
      counts.contacts,
      queueTotals,
      previews,
      locationIds.length,
      valueStats,
      snoozes,
      needsActionDateEnabled,
    ],
  )

  return {
    user,
    workspace,
    greeting: greetingForNow(user?.name),
    queues,
    momentum,
    valueStats,
    loadingValues,
  }
}
